/**
 * EP-26 (B7.1/B7.2) — Aviso de irreversibilidad de la expulsión de un
 * miembro y traducción de los `errorCode` que devuelve el backend.
 *
 * `DELETE /members/{id}` / `DELETE /families/{familyId}/members/{memberId}`
 * ya no desactivan: BORRAN de forma irreversible la cuenta del miembro (si
 * la tiene) y sus datos privados (mr-11, `member-removal` spec). Antes de
 * B7 la interfaz seguía diciendo "Quitar de la familia" — este módulo es el
 * único punto de verdad del texto que cierra ese desfase, compartido por
 * `apps/web` y `apps/mobile` (design §5.4).
 *
 * Función PURA y SÍNCRONA: cero I/O. Vive en `application/` (no en
 * `domain/`) solo porque agrupa junto a `describeMemberRemovalError`, que
 * traduce códigos de error de una respuesta HTTP — ninguna de las dos hace
 * la llamada de red, eso sigue siendo responsabilidad del caller.
 */

export interface MemberRemovalWarningInput {
  memberName: string
  familyName?: string
  /** false ⇒ el miembro no tiene cuenta: no hay credenciales ni sesiones que borrar. */
  hasUserAccount?: boolean
  /** Nombre del rol familiar (ADMIN, PADRE, MADRE, TUTOR…) para el aviso de responsables. */
  familyRole?: string
  /** Administra la familia (marca aparte del rol, V50): también le hace "responsable". */
  isFamilyAdmin?: boolean
}

export interface MemberRemovalWarning {
  title: string
  message: string
  confirmText: string
  cancelText: string
}

/** Roles familiares que hacen a alguien "responsable" (bloqueo de último tutor, D4). */
const QUALIFIED_FAMILY_ROLES = ['ADMIN', 'PADRE', 'MADRE', 'TUTOR']

/**
 * Construye el aviso de irreversibilidad para el diálogo de confirmación.
 * Dos variantes según `hasUserAccount` (por defecto, con cuenta: `false` es
 * la única forma explícita de pedir la variante "sin cuenta" — `undefined`
 * se trata como "sí tiene", igual que hace hoy `MemberDetailSheet.vue` con
 * `!member.isUser`).
 */
export function buildMemberRemovalWarning(input: MemberRemovalWarningInput): MemberRemovalWarning {
  const { memberName, familyName, hasUserAccount, familyRole, isFamilyAdmin } = input
  const hasAccount = hasUserAccount !== false

  const title = familyName
    ? `¿Eliminar a ${memberName} de la familia "${familyName}"?`
    : `¿Eliminar a ${memberName} de la familia?`

  let message: string
  let confirmText: string

  if (hasAccount) {
    message = [
      'Esto NO es una desactivación. Se borra su cuenta de Famindly y sus datos privados: acceso, ' +
        'contraseña, sesiones, dispositivos, recordatorios personales y sus mensajes del chat familiar.',
      'El historial compartido de la familia se conserva: sus tareas y asignaciones seguirán ahí, a ' +
        'nombre de «Miembro retirado».',
      'Esta acción es IRREVERSIBLE. No podrás recuperar su cuenta ni sus datos, y tendrá que ' +
        'registrarse de cero para volver.',
    ].join('\n\n')
    confirmText = 'Sí, eliminar y borrar su cuenta'
  } else {
    message = [
      'Esto NO es una desactivación. Se borran sus datos personales: nombre, correo, teléfono y fecha ' +
        'de nacimiento.',
      'El historial compartido de la familia se conserva: sus tareas y asignaciones seguirán ahí, a ' +
        'nombre de «Miembro retirado».',
      'Esta acción es IRREVERSIBLE.',
    ].join('\n\n')
    confirmText = 'Sí, eliminar sus datos'
  }

  if (isFamilyAdmin === true || (familyRole && QUALIFIED_FAMILY_ROLES.includes(familyRole))) {
    message +=
      '\n\n' +
      `Además, ${memberName} es una de las personas responsables de la familia. Si fuese la última, el ` +
      'servidor rechazará la operación para no dejar la familia sin responsables.'
  }

  return { title, message, confirmText, cancelText: 'Cancelar' }
}

/**
 * Traduce el `errorCode` de un fallo de expulsión a un mensaje específico
 * (mr-12): un 409 de "último tutor" no puede ser indistinguible de
 * cualquier otro fallo para quien lo provoca.
 */
export function describeMemberRemovalError(errorCode?: string, fallback?: string): string {
  switch (errorCode) {
    case 'MEMBER_REMOVAL_LAST_HOLDER':
      return 'No se puede eliminar: es la última persona responsable de la familia. Asigna antes ese rol a otra persona.'
    case 'MEMBER_REMOVAL_SELF_NOT_ALLOWED':
      return 'No puedes eliminarte a ti desde aquí. Hazlo desde los ajustes de tu cuenta, donde se te pedirá la contraseña.'
    case 'MEMBER_REMOVAL_ALREADY_PENDING':
      return 'Esta persona ya tiene un borrado de cuenta en curso. Se completará al vencer su plazo.'
    default:
      return fallback || 'No se pudo eliminar al miembro.'
  }
}
